import React, { useState, useCallback, useEffect, useRef } from "react";
import { format, parse, isValid, addDays, min, max } from "date-fns";
import { mdiCalendarRange, mdiClose } from "@mdi/js";
import { cn } from "../../styles";
import { LocalizationScope } from "../../util/LocalizationScope";
import { Button, ButtonVariant, Typography } from "../../atoms";
import { PanelColor, ThemeColor } from "../../props";
import { processErrorContent } from "./helper";
import { Input } from "../Input";
import { BaseFormFieldProps, FormFieldConstraint } from "./index";
import { Calendar } from "../Calendar";
import { Dialog } from "../../layouts";

export interface DateRangeFieldConstraint extends FormFieldConstraint {
  daterange?: {
    beginEarliest?: Date;
    beginLatest?: Date;
    endEarliest?: Date;
    endLatest?: Date;
    minLength?: number;
    maxLength?: number;
  };
}

export interface QuickRange {
  label: string;
  range: () => [Date, Date];
}

export interface FormFieldDateRangeProps extends Omit<
  BaseFormFieldProps,
  "constraints"
> {
  value: [Date, Date] | null;
  data?: { input?: { begin?: string; end?: string } };
  onChangeField: (
    id: string,
    value: [Date, Date] | null,
    opts?: {
      data?: { input?: { begin?: string; end?: string } };
      [key: string]: any;
    },
  ) => void;
  constraints: DateRangeFieldConstraint;
  format?: string;
  dateOnly?: boolean;
  quickRanges?: QuickRange[];
  placeholder?: string;
}

const DEFAULT_DATE_FORMAT = "dd.MM.yyyy";

function formatToMask(fmt: string): string {
  return (fmt || DEFAULT_DATE_FORMAT).replace(/[yMdHhms]/g, "9");
}

export const DateRange: React.FC<FormFieldDateRangeProps> = ({
  className,
  id,
  value,
  data,
  onChangeField,
  label = "Label",
  localKey,
  errors = [],
  touched,
  helper,
  helperKey,
  constraints,
  format: dateFormat = DEFAULT_DATE_FORMAT,
  dateOnly = true,
  quickRanges,
  placeholder,
  disabled,
  keepHeight,
  noLabel,
  dense,
  readOnly,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState<[Date, Date]>([
    new Date(),
    addDays(new Date(), 3),
  ]);

  const error = touched && errors[0];
  const mask = formatToMask(dateFormat);
  const showClear =
    !disabled &&
    !readOnly &&
    !constraints.presence &&
    (!!value || !!(data?.input?.begin || data?.input?.end));

  const displayBegin =
    data?.input?.begin ??
    (value?.[0] && isValid(value[0]) ? format(value[0], dateFormat) : "");
  const displayEnd =
    data?.input?.end ??
    (value?.[1] && isValid(value[1]) ? format(value[1], dateFormat) : "");

  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (value && isValid(value[0]) && isValid(value[1]) && !data?.input) {
        (onChangeField as any)(id, value, {
          data: {
            input: {
              begin: format(value[0], dateFormat),
              end: format(value[1], dateFormat),
            },
          },
          disableCallback: true,
        });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChangeBegin = useCallback(
    (raw: string) => {
      const parsed =
        raw && !raw.includes("_") ? parse(raw, dateFormat, new Date()) : null;
      const date = parsed && isValid(parsed) ? parsed : null;
      const newValue = date
        ? ([date, value?.[1] ?? addDays(date, 3)] as [Date, Date])
        : null;
      onChangeField(id, newValue, {
        data: { input: { begin: raw, end: displayEnd } },
      });
    },
    [dateFormat, id, onChangeField, value, displayEnd],
  );

  const handleInputChangeEnd = useCallback(
    (raw: string) => {
      const parsed =
        raw && !raw.includes("_") ? parse(raw, dateFormat, new Date()) : null;
      const date = parsed && isValid(parsed) ? parsed : null;
      const newValue = date
        ? ([value?.[0] ?? new Date(), date] as [Date, Date])
        : null;
      onChangeField(id, newValue, {
        data: { input: { begin: displayBegin, end: raw } },
      });
    },
    [dateFormat, id, onChangeField, value, displayBegin],
  );

  const handleClear = () =>
    onChangeField(id, null, { data: { input: { begin: "", end: "" } } });

  const handleOpenDialog = () => {
    if (value && isValid(value[0]) && isValid(value[1])) {
      setDialogValue([value[0], value[1]]);
    } else {
      setDialogValue([new Date(), addDays(new Date(), 3)]);
    }
    setDialogOpen(true);
  };

  const handleDialogOk = () => {
    onChangeField(id, dialogValue, {
      data: {
        input: {
          begin: format(dialogValue[0], dateFormat),
          end: format(dialogValue[1], dateFormat),
        },
      },
    });
    setDialogOpen(false);
  };

  const handleDialogClear = () => {
    onChangeField(id, null, { data: { input: { begin: "", end: "" } } });
    setDialogOpen(false);
  };

  const handleQuickRange = (qr: QuickRange) => {
    setDialogValue(qr.range());
  };

  const c = constraints.daterange;

  const beginMaxValues = [c?.beginLatest, addDays(dialogValue[1], -1)].filter(
    (v): v is Date => !!v,
  );
  const beginMax = beginMaxValues.length ? min(beginMaxValues) : undefined;

  const endMinValues = [c?.endEarliest, addDays(dialogValue[0], 1)].filter(
    (v): v is Date => !!v,
  );
  const endMin = endMinValues.length ? max(endMinValues) : undefined;

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex">
          <Typography.Label
            htmlFor={id}
            className={cn(
              "block font-medium select-none",
              dense ? "mb-0.5 text-xs" : "mb-1 text-sm",
            )}
            content={label || { local: id }}
          />
          {!!constraints.presence && (
            <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
          )}
        </div>
      )}
      <div className="flex gap-1 items-center">
        <Input
          className="grow"
          id={`${id}-begin`}
          value={displayBegin}
          onChange={handleInputChangeBegin}
          placeholder={placeholder ?? dateFormat}
          disabled={disabled}
          readOnly={readOnly}
          required={!!constraints.presence}
          dense={dense}
          error={!!error}
          mask={mask}
          aria-invalid={!!error}
        />
        <Typography.Span muted className="text-sm select-none">
          ⇒
        </Typography.Span>
        <Input
          className="grow"
          id={`${id}-end`}
          value={displayEnd}
          onChange={handleInputChangeEnd}
          placeholder={placeholder ?? dateFormat}
          disabled={disabled}
          readOnly={readOnly}
          required={!!constraints.presence}
          dense={dense}
          error={!!error}
          mask={mask}
          aria-invalid={!!error}
        />
        <Button
          variant={ButtonVariant.text}
          color={ThemeColor.gray}
          onClick={handleOpenDialog}
          iconPath={mdiCalendarRange}
          dense
          muted
          tabIndex={-1}
          disabled={disabled || readOnly}
        />
        {showClear && (
          <Button
            variant={ButtonVariant.text}
            color={ThemeColor.gray}
            onClick={handleClear}
            iconPath={mdiClose}
            dense
            muted
            tabIndex={-1}
          />
        )}
      </div>
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        minWidth={0}
        color={PanelColor.level2}
      >
        <Dialog.Content scroll={false} className="!p-0 !overflow-hidden">
          <div className="flex">
            {quickRanges && quickRanges.length > 0 && (
              <div className="flex flex-col gap-1 p-3 border-r border-back-200 min-w-[140px]">
                <Typography.Span className="text-xs font-medium mb-1" muted>
                  Quick Select
                </Typography.Span>
                {quickRanges.map((qr) => (
                  <Button
                    key={qr.label}
                    variant={ButtonVariant.text}
                    color={ThemeColor.primary}
                    onClick={() => handleQuickRange(qr)}
                    className="!justify-start text-sm"
                    dense
                  >
                    {qr.label}
                  </Button>
                ))}
              </div>
            )}
            <div className="flex items-start gap-2 p-2">
              <Calendar
                className="rounded-xl overflow-hidden"
                value={dialogValue[0]}
                rangeEndValue={dialogValue[1]}
                onChange={(d) => setDialogValue((v) => [d, v[1]])}
                dateOnly={dateOnly}
                minValue={c?.beginEarliest}
                maxValue={beginMax}
              />
              <div className="flex items-center self-center text-gray-400 select-none px-1">
                <Typography.Span className="text-lg">⇒</Typography.Span>
              </div>
              <Calendar
                className="rounded-xl overflow-hidden"
                value={dialogValue[1]}
                rangeBeginValue={dialogValue[0]}
                onChange={(d) => setDialogValue((v) => [v[0], d])}
                dateOnly={dateOnly}
                minValue={endMin}
                maxValue={c?.endLatest}
              />
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <Dialog.Actions>
            <Button
              variant={ButtonVariant.text}
              color={ThemeColor.gray}
              onClick={handleDialogClear}
              dense
            >
              <Typography.Span content={{ local: "Clear" }} />
            </Button>
            <Button
              variant={ButtonVariant.text}
              color={ThemeColor.gray}
              onClick={() => setDialogOpen(false)}
              dense
            >
              <Typography.Span content={{ local: "Cancel" }} />
            </Button>
            <Button
              variant={ButtonVariant.contained}
              color={ThemeColor.primary}
              onClick={handleDialogOk}
              dense
            >
              <Typography.Span content={{ local: "OK" }} />
            </Button>
          </Dialog.Actions>
        </Dialog.Footer>
      </Dialog>
    </div>
  );
};

DateRange.displayName = "FormField.DateRange";
